class CollectionItem extends HTMLElement {
  static cartRequestQueue = Promise.resolve();
  static sharedCart = null;
  static sharedCartFetchedAt = 0;
  static CART_CACHE_MS = 1500;

  constructor() {
    super();

    this.setLoadingState(false);
    this.debounceTimeout = null;

    const variantsNode = this.querySelector('.product-json');
    const cartItemsNode = this.querySelector('.cart-items-json');

    try {
      this.variantsJSON = variantsNode ? JSON.parse(variantsNode.textContent) : [];
    } catch (e) {
      console.error('Failed to parse variants JSON:', e);
      this.variantsJSON = [];
    }

    try {
      this.cartItemsJSON = cartItemsNode ? JSON.parse(cartItemsNode.textContent) : [];
    } catch (e) {
      console.error('Failed to parse cart items JSON:', e);
      this.cartItemsJSON = [];
    }

    // Seed shared cart cache from markup if available
    if (!CollectionItem.sharedCart && this.cartItemsJSON.length) {
      CollectionItem.sharedCart = {
        items: this.cartItemsJSON.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
          key: item.line_key,
          variant_title: item.title,
        })),
      };
      CollectionItem.sharedCartFetchedAt = Date.now();
    }

    this.boundChangeVariant = this.changeVariant.bind(this);
    this.boundChangeQuantity = this.changeQuantity.bind(this);

    this.initVariantRadios();
    this.initQuantityInputs();
  }

  setLoadingState(isLoading, silently = false) {
    this.isLoading = isLoading;
    this.setAttribute('data-is-loading', String(isLoading));

    if (!silently) {
      this.classList.toggle('is-loading', isLoading);
    }
  }

  initVariantRadios() {
    this.variantRadios = this.querySelectorAll('input[type="radio"]');

    if (this.variantRadios.length > 0) {
      this.variantRadios.forEach((radio) => {
        radio.removeEventListener('change', this.boundChangeVariant);
        radio.addEventListener('change', this.boundChangeVariant);
      });

      const checkedRadio = this.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        this.currentVariantId = parseInt(checkedRadio.value, 10);
      }
    } else {
      this.currentVariantId = this.variantsJSON[0]?.id || null;
    }

    this.variantSelect = this.querySelector('.collection_item-variant-select');
    if (this.variantSelect) {
      this.variantSelect.removeEventListener('change', this.boundChangeVariant);
      this.variantSelect.addEventListener('change', this.boundChangeVariant);

      if (!this.currentVariantId && this.variantSelect.value) {
        this.currentVariantId = parseInt(this.variantSelect.value, 10);
      }
    }

    this.currentVariant = this.variantsJSON.find(
      (variant) => variant.id === this.currentVariantId
    );
  }

  initQuantityInputs() {
    const incrementNode = this.querySelector('[data-increment]');

    try {
      this.quantityRules = incrementNode
        ? JSON.parse(incrementNode.dataset.increment)
        : { increment: 1, max: null };
    } catch (e) {
      console.error('Failed to parse quantity rules:', e);
      this.quantityRules = { increment: 1, max: null };
    }

    if (!this.quantityRules.increment || this.quantityRules.increment < 1) {
      this.quantityRules.increment = 1;
    }

    this.quantityInputs = this.querySelectorAll('input[type="number"]');
    this.quantityButtons = this.querySelectorAll('.quantity__button');

    this.quantityInputs.forEach((input) => {
      input.removeEventListener('change', this.boundChangeQuantity);
      input.addEventListener('change', this.boundChangeQuantity);
    });

    this.quantityButtons.forEach((button) => {
      button.removeEventListener('click', this.boundChangeQuantity);
      button.addEventListener('click', this.boundChangeQuantity);
    });
  }

  normalizeJsRoute(route) {
    if (!route) return '';
    return route.endsWith('.js') ? route : `${route}.js`;
  }

  getRootUrl() {
    const root = window.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isCloudflareChallenge(text = '') {
    return (
      text.includes('Verifying your connection') ||
      text.includes('__cf_chl_tk') ||
      text.includes('Enable JavaScript and cookies to continue')
    );
  }

  async safeFetchJSON(url, options = {}, attempt = 0) {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    const text = await response.text();
    const contentType = response.headers.get('content-type') || '';

    // Retry transient cart conflicts / throttling
    if ((response.status === 409 || response.status === 429) && attempt < 3) {
      const delay = 800 * Math.pow(2, attempt);
      await this.sleep(delay);
      return this.safeFetchJSON(url, options, attempt + 1);
    }

    // Cloudflare / anti-bot HTML challenge
    if (this.isCloudflareChallenge(text)) {
      throw new Error('Cart requests are being rate-limited or challenged by Cloudflare. Please slow down cart requests.');
    }

    let json;
    try {
      json = JSON.parse(text);
    } catch (e) {
      throw new Error(
        `Invalid JSON response from server.\nURL: ${url}\nStatus: ${response.status}\nContent-Type: ${contentType}\nResponse: ${text}`
      );
    }

    if (!response.ok) {
      throw new Error(json.description || json.message || json.error || 'Request failed');
    }

    return json;
  }

  async fetchCart(force = false) {
    const now = Date.now();

    if (
      !force &&
      CollectionItem.sharedCart &&
      now - CollectionItem.sharedCartFetchedAt < CollectionItem.CART_CACHE_MS
    ) {
      return CollectionItem.sharedCart;
    }

    const cart = await this.safeFetchJSON(`${this.getRootUrl()}cart.js`, {
      method: 'GET',
    });

    CollectionItem.sharedCart = cart;
    CollectionItem.sharedCartFetchedAt = Date.now();

    return cart;
  }

  syncLocalCartItems(cart) {
    CollectionItem.sharedCart = cart;
    CollectionItem.sharedCartFetchedAt = Date.now();

    this.cartItemsJSON = (cart?.items || []).map((item) => ({
      title: item.variant_title,
      quantity: item.quantity,
      variant_id: item.variant_id,
      line_key: item.key,
    }));
  }

  getCachedVariantQuantity(variantId) {
    const items = CollectionItem.sharedCart?.items || [];
    const item = items.find((entry) => entry.variant_id === variantId);
    return item ? item.quantity : 0;
  }

  async fetchLiveProduct(variantId = this.currentVariantId) {
    const url = new URL(
      `${window.top.location.origin}${this.getRootUrl()}products/${this.dataset.productHandle}`
    );

    url.searchParams.set('variant', variantId);
    url.searchParams.set('view', 'ajax-collection-item');

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'text/html',
      },
    });

    const html = await response.text();
    const parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
  }

  async changeVariant(event) {
    this.setLoadingState(true);

    this.currentVariantId = parseInt(event.target.value, 10);
    this.currentVariant = this.variantsJSON.find(
      (variant) => variant.id === this.currentVariantId
    );

    try {
      const doc = await this.fetchLiveProduct();

      const newSku = doc.querySelector(
        `.ajax-sku[data-variant-id="${this.currentVariantId}"]`
      );
      const currentSku = this.querySelector('.ajax-sku');
      if (newSku && currentSku) {
        currentSku.textContent = newSku.textContent;
      }

      const newInventory = doc.querySelector(
        `.ajax-inventory[data-variant-id="${this.currentVariantId}"]`
      );
      const currentInventory = this.querySelector('.ajax-inventory');
      if (newInventory && currentInventory) {
        currentInventory.innerHTML = newInventory.innerHTML;
      }

      const newQuantityBlock = doc.querySelector(
        `.ajax-quantity[data-variant-id="${this.currentVariantId}"]`
      );
      const currentQuantityBlock = this.querySelector('.ajax-quantity');
      if (currentQuantityBlock) {
        currentQuantityBlock.innerHTML = newQuantityBlock
          ? newQuantityBlock.innerHTML
          : '<span>No inventory available.</span>';
      }

      this.initQuantityInputs();

      // Use cached cart first; avoid hitting /cart.js on every variant switch
      const quantityInput = this.querySelector('input[type="number"]');
      if (quantityInput) {
        quantityInput.value = this.getCachedVariantQuantity(this.currentVariantId);
      }
    } catch (error) {
      console.error('Error fetching live product or updating DOM:', error);
    }

    document.dispatchEvent(new Event('variantChanged'));
    this.setLoadingState(false);
  }

  changeQuantity(event) {
    this.setLoadingState(true, true);

    const isClick = event.type === 'click';
    const quantityInput = this.querySelector('input[type="number"]');

    if (!quantityInput) {
      this.setLoadingState(false, true);
      return;
    }

    const clickedButton = event.target.closest('.quantity__button');
    const increment = this.quantityRules.increment || 1;

    if (isClick && clickedButton?.name === 'minus') {
      quantityInput.value = parseInt(quantityInput.value || 0, 10) - increment;
    } else if (isClick && clickedButton?.name === 'plus') {
      quantityInput.value = parseInt(quantityInput.value || 0, 10) + increment;
    } else {
      const rawValue = parseInt(event.target.value || 0, 10) || 0;
      const roundedValue = Math.round(rawValue / increment) * increment;
      quantityInput.value = roundedValue;

      if (parseInt(quantityInput.value, 10) === 0 && rawValue > 0) {
        quantityInput.value = increment;
      }
    }

    if (parseInt(quantityInput.value, 10) < 0) {
      quantityInput.value = 0;
    }

    if (
      this.quantityRules.max &&
      parseInt(quantityInput.value, 10) > this.quantityRules.max
    ) {
      quantityInput.value = this.quantityRules.max;
    }

    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.enqueueCartUpdate();
    }, isClick ? 900 : 500);
  }

  enqueueCartUpdate() {
    CollectionItem.cartRequestQueue = CollectionItem.cartRequestQueue
      .then(() => this.updateCart())
      .catch((error) => {
        console.error('Queued cart update failed:', error);
      });

    return CollectionItem.cartRequestQueue;
  }

  async updateCart() {
    try {
      const quantityInput = this.querySelector('input[type="number"]');
      if (!quantityInput) {
        this.setLoadingState(false, true);
        return;
      }

      const quantity = parseInt(quantityInput.value || 0, 10);

      // Use cached cart state first; only fetch if we have nothing cached
      let currentCart = CollectionItem.sharedCart;
      if (!currentCart) {
        currentCart = await this.fetchCart(true);
      }

      const existingItem = currentCart.items?.find(
        (item) => item.variant_id === this.currentVariantId
      );

      const updateMethod = existingItem ? 'update' : 'add';

      if (updateMethod === 'add' && quantity === 0) {
        this.setLoadingState(false, true);
        return;
      }

      const cart = await this.updateCartPromise(updateMethod, quantity);
      this.syncLocalCartItems(cart);

      this.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: { silently: true },
        })
      );

      const doc = await this.fetchLiveProduct();
      const notificationBar = doc.querySelector(
        '.ajax-notification .cart_item--notification'
      );
      const currentNotificationBar = this.querySelector(
        '.ajax-notification .cart_item--notification'
      );

      if (notificationBar && currentNotificationBar) {
        if (updateMethod === 'add') {
          currentNotificationBar.classList.add('show');
          currentNotificationBar.innerHTML = notificationBar.innerHTML;
        } else if (quantity === 0 && this.cartItemsJSON.length === 0) {
          currentNotificationBar.classList.remove('show');
          currentNotificationBar.innerHTML = '';
        } else {
          currentNotificationBar.innerHTML = notificationBar.innerHTML;
        }
      }
    } catch (error) {
      console.error(error);
      // Optional: replace alert with a toast/snackbar
      alert(error.message || error);
    }

    this.setLoadingState(false, true);
  }

  async updateCartPromise(updateMethod, quantity) {
    if (updateMethod === 'add' && quantity === 0) {
      return this.fetchCart(true);
    }

    if (updateMethod === 'update') {
      const cart = await this.safeFetchJSON(`${this.getRootUrl()}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [this.currentVariantId]: quantity,
          },
        }),
      });

      return cart;
    }

    if (updateMethod === 'add' && quantity > 0) {
      const addUrl = this.normalizeJsRoute(window.routes.cart_add_url);

      // add.js response is not always the full cart, so only fetch cart once after mutation
      await this.safeFetchJSON(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: this.currentVariantId,
              quantity: quantity,
            },
          ],
        }),
      });

      return this.fetchCart(true);
    }

    return this.fetchCart(true);
  }
}

customElements.define('collection-item', CollectionItem);